import Vue from 'vue';
import Vuex from 'vuex';
import RemaxService from '@/services/RemaxService';
import { SavedFilter } from '@/models/SavedFilter';
import { Filter } from '@/models/Filter';
import { MinMax } from '@/models/MinMax';
import L from 'leaflet';
import FilterUtil from '@/utils/FilterUtil';

Vue.use(Vuex);

export interface FilterState {
    isFilterLoading: boolean;
    savedFilters: SavedFilter[];
    activeFilter: Filter;
    spatialFilter: L.GeoJSON | null;
    filterCount: number | null;
}

export default {
    state: {
        isFilterLoading: false,
        savedFilters: [],
        activeFilter: new Filter({
            price: {
                min: 0,
                max: 99999999,
            },
            beds: {
                min: 0,
                max: 99,
            },
            baths: {
                min: 0,
                max: 99,
            },
            mla: {
                min: 0,
                max: 99999,
            },
            tla: {
                min: 0,
                max: 99999,
            },
            lotSize: {
                min: 0,
                max: 99999999,
            },
            age: {
                min: 0,
                max: 999,
            },
            type: {value: []},
            style: {value: []},
            class: {value: []},
            status: {value: ['1', '3']}, // active, pending default shown
            heatType: {value: []},
            waterfront: {value: null}, // null = false, 1 = true
            garage:  {value: null}, // null = false, 1 = true
            schoolCatchmentId: {value: null},
        }),
        spatialFilter: null,
        filterCount: null,
    } as FilterState,
    mutations: {
        SET_ACTIVE_FILTER(state: FilterState, filter: Filter) {
            state.activeFilter = filter;
        },
        SET_PRICE(state: FilterState, priceMinMax: MinMax) {
            Vue.set(state.activeFilter, 'Price', priceMinMax);
        },
        SET_BEDS(state: FilterState, bedsMinMax: MinMax) {
            Vue.set(state.activeFilter, 'Beds', bedsMinMax);
        },
        SET_BATHS(state: FilterState, bathsMinMax: MinMax) {
            Vue.set(state.activeFilter, 'Baths', bathsMinMax);
        },
        SET_MLA(state: FilterState, mlaMinMax: MinMax) {
            Vue.set(state.activeFilter, 'Mla', mlaMinMax);
        },
        SET_TLA(state: FilterState, tlaMinMax: MinMax) {
            Vue.set(state.activeFilter, 'Tla', tlaMinMax);
        },
        SET_LOT(state: FilterState, lotMinMax: MinMax) {
            Vue.set(state.activeFilter, 'LotSize', lotMinMax);
        },
        SET_AGE(state: FilterState, ageMinMax: MinMax) {
            Vue.set(state.activeFilter, 'Age', ageMinMax);
        },
        SET_STATUS(state: FilterState, status: string[]) {
            Vue.set(state.activeFilter, 'Status', {Value: status});
        },
        SET_CLASS(state: FilterState, classes: string[]) {
            Vue.set(state.activeFilter, 'Class', {Value: classes});
        },
        SET_TYPE(state: FilterState, types: string[]) {
            Vue.set(state.activeFilter, 'Type', {Value: types});
        },
        SET_STYLE(state: FilterState, styles: string[]) {
            Vue.set(state.activeFilter, 'Style', {Value: styles});
        },
        SET_HEAT(state: FilterState, heat: string[]) {
            Vue.set(state.activeFilter, 'HeatType', {Value: heat});
        },
        SET_GARAGE(state: FilterState, isGarage: boolean) {
            Vue.set(state.activeFilter, 'Garage', {Value: isGarage ? 1 : null});
        },
        SET_WATERFRONT(state: FilterState, isWaterfront: boolean) {
            Vue.set(state.activeFilter, 'Waterfront', {Value: isWaterfront ? 1 : null});
        },
        SET_SPATIAL_FILTER(state: FilterState, spatialFilter: L.GeoJSON | null) {
            state.spatialFilter = spatialFilter;
        },
        SET_SCHOOL_CATCHMENT_ID(state: FilterState, schoolCatchmentId: string) {
            Vue.set(state.activeFilter, 'SchoolCatchmentId', {value: schoolCatchmentId});
        },
        CLEAR_ACTIVE_FILTER(state: FilterState) {
            state.activeFilter = new Filter({
                price: {
                    min: 0,
                    max: 99999999,
                },
                beds: {
                    min: 0,
                    max: 99,
                },
                baths: {
                    min: 0,
                    max: 99,
                },
                mla: {
                    min: 0,
                    max: 99999,
                },
                tla: {
                    min: 0,
                    max: 99999,
                },
                lotSize: {
                    min: 0,
                    max: 99999999,
                },
                age: {
                    min: 0,
                    max: 999,
                },
                type: {value: []},
                style: {value: []},
                class: {value: []},
                status: {value: ['1', '3']}, // active, pending default shown
                heatType: {value: []},
                waterfront: {value: null}, // null = false, 1 = true
                garage: {value: null}, // null = false, 1 = true
                schoolCatchmentId: {value: null},
            });
        },
        SET_FILTER_COUNT(state: FilterState, filterCount: number) {
            state.filterCount = filterCount;
        },
        SET_SAVED_FILTERS(state: FilterState, filters: SavedFilter[]) {
            state.savedFilters = filters;
        },
        ADD_SAVED_FILTER(state: FilterState, filter: SavedFilter) {
            state.savedFilters.push(filter);
        },
        REMOVE_SAVED_FILTER(state: FilterState, mfid: number) {
            state.savedFilters = state.savedFilters.filter((savedFilter: SavedFilter) => savedFilter.MFID !== mfid);
        },
        SET_FILTER_LOADING(state: FilterState, isLoading: boolean) {
            state.isFilterLoading = isLoading;
        },
    },
    actions: {
        // was EVENT_filter_changed
        filterChanged(context: any) {
            // TODO no mutations needed?
        },
        clearFilters(context: any) {
            // TODO mutate all filters back to default state
        },
        showFilterDialog(context: any) {
            // no mutations
        },
        showFilterSaveDialog(context: any) {
            // no mutations
        },
        showFilterManager(context: any) {
            // no mutations
        },
        hideFilterManager(context: any) {
            // no mutations
        },
        setPriceMin(context: any, priceMin: number) {
            context.commit('SET_PRICE_MIN', priceMin);
        },
        setPriceMax(context: any, priceMax: number) {
            context.commit('SET_PRICE_MAX', priceMax);
        },
        setBedsMin(context: any, bedsMin: number) {
            context.commit('SET_BEDS_MIN', bedsMin);
        },
        setBedsMax(context: any, bedsMax: number) {
            context.commit('SET_BEDS_MAX', bedsMax);
        },
        setBaths(context: any, bathsMinMax: MinMax) {
            context.commit('SET_BATHS', bathsMinMax);
        },
        setMla(context: any, mlaMinMax: MinMax) {
            context.commit('SET_MLA', mlaMinMax);
        },
        setTla(context: any, tlaMinMax: MinMax) {
            context.commit('SET_TLA', tlaMinMax);
        },
        setLot(context: any, lotMinMax: MinMax) {
            context.commit('SET_LOT', lotMinMax);
        },
        setAge(context: any, ageMinMax: MinMax) {
            context.commit('SET_AGE', ageMinMax);
        },
        setStatus(context: any, status: string[]) {
            context.commit('SET_STATUS', status);
        },
        setGarage(context: any, isGarage: boolean) {
            context.commit('SET_GARAGE', isGarage);
        },
        setWaterfront(context: any, isWaterfront: boolean) {
            context.commit('SET_WATERFRONT', isWaterfront);
        },
        setClass(context: any, classes: string[]) {
            context.commit('SET_CLASS', classes);
        },
        setType(context: any, types: string[]) {
            context.commit('SET_TYPE', types);
        },
        setStyle(context: any, styles: string[]) {
            context.commit('SET_STYLE', styles);
        },
        setHeat(context: any, heat: string[]) {
            context.commit('SET_HEAT', heat);
        },
        setSpatialFilter(context: any, spatialFilter: L.GeoJSON | null) {
            context.commit('SET_SPATIAL_FILTER', spatialFilter);
        },
        setSchoolCatchmentId(context: any, schoolCatchmentId: string) {
            context.commit('SET_SCHOOL_CATCHMENT_ID', schoolCatchmentId);
        },
        saveActiveFilter(context: any, payload: any) {
            const newFilter = new SavedFilter({
                Filter: context.state.activeFilter.toJson(),
                Notifications: payload.Notifications,
                Name: payload.Name,
                Polygon: null,
                MultiPolygon: null,
            });
            let newPoly = null;
            if (payload.SpatialFilter && payload.SpatialFilter.geometry) {
                newPoly = payload.SpatialFilter.geometry.coordinates[0].map((coord: any) => ({lng: coord[0], lat: coord[1]}));
            }
            if (payload.SpatialFilter && payload.SpatialFilter.features) {
                newPoly = payload.SpatialFilter.features[0].geometry.coordinates[0].map((coord: any) => ({lng: coord[0], lat: coord[1]}));
            }
            newFilter.Polygon = newPoly;

            RemaxService.saveFilterData(newFilter.toJson())
            .then((response) => {
                newFilter.MFID = response.data;
                // on return add the filter that has MFID, dispatch success/error
                context.commit('ADD_SAVED_FILTER', newFilter);
                context.dispatch('saveFilterSuccess');
            });
        },
        saveFilterSuccess(context: any) {
            //
        },
        setActiveFilter(context: any, filter: Filter) {
            context.commit('SET_ACTIVE_FILTER', filter);
        },
        clearActiveFilter(context: any) {
            // FilterDialog subscribes and clears any map layers
            context.commit('SET_SPATIAL_FILTER', null);
            context.commit('CLEAR_ACTIVE_FILTER');
        },
        setFilterNumber(context: any, filterCount: number) {
            context.commit('SET_FILTER_COUNT', filterCount);
        },
        getFilterData(context: any) {
            context.commit('SET_FILTER_LOADING', true);
            RemaxService.getFilterData()
            .then((response) => {
                const filters = response.data;
                const savedFilters: SavedFilter[] = [];
                for (let i = 0; i < filters.length; i++) {
                    const filter = new SavedFilter({
                        Name: filters[i].name,
                        Notifications: filters[i].notifications,
                        Polygon: filters[i].polygon,
                        MultiPolygon: filters[i].multiPolygon,
                        MFID: filters[i].mfid,
                        Filter: filters[i].filter,
                    });
                    savedFilters.push(filter);
                }

                context.commit('SET_SAVED_FILTERS', savedFilters);
                context.commit('SET_FILTER_LOADING', false);
            })
            .catch((error) => {
                console.error(error);
                context.commit('SET_FILTER_LOADING', false);
                context.dispatch('notify', 'Failed to get filter data');
            });
        },
        // EVENT_filter_remove
        removeSavedFilter(context: any, mfid: number) {
            RemaxService.deleteFilterData(mfid);
            context.commit('REMOVE_SAVED_FILTER', mfid);
        },
        // EVENT_filter_restore
        restoreFilter(context: any, mfid: number) {
            const savedfilter: SavedFilter = context.state.savedFilters.find((filter: SavedFilter) => filter.MFID === mfid);
            const updateFilter = savedfilter.Filter;
            if (savedfilter.Polygon) {
                context.dispatch('setSpatialFilter', JSON.stringify(savedfilter.getGeoJson()));
                } else {
                context.dispatch('clearActiveFilter');
            }
            context.commit('SET_ACTIVE_FILTER', updateFilter);
        },
    },
};
